/**
 * A chat message's parts: persisted history → `UIMessage`, and any
 * `UIMessage` (streamed or hydrated) → what `MessageBubble` renders.
 *
 * Extracted from `ChatInterface`. The backend persists a flat
 * `{content, sources_used, images, visuals}` per message; the AI SDK works in
 * ordered `parts`. History is mapped to the same parts the stream produces -
 * a text part, one `data-citation` per source, one `data-image` per picture -
 * so both paths render through one function.
 */
import type { UIMessage } from 'ai';
import type { ResolvedLibraryImage } from '@/components/core/markdown/markdown';
import type {
  ChatSourceCitation,
  ConversationHistoryMessage,
} from '@/lib/api-client';
import type { Visual } from '@/types/visual';

/** The `data` of a `data-image` part (backend `sse.image_part`). */
export interface ChatImageData extends ResolvedLibraryImage {
  id: string;
  kind: 'library' | 'source' | 'visual';
  credit?: string;
  visual?: Visual | null;
}

type Part = UIMessage['parts'][number];

function citationPart(s: ChatSourceCitation, i: number): Part {
  return {
    type: 'data-citation',
    id: s.chunk_id,
    data: {
      index: s.marker ?? i + 1,
      marker: s.marker ?? null,
      chunk_id: s.chunk_id,
      source_id: s.source_id,
      source_name: s.source_name,
      source_type: s.source_type ?? 'text',
      text_preview: s.text_preview,
      url: s.url ?? null,
      file_key: s.file_key ?? null,
      page: s.page ?? null,
      relevance_score: s.relevance_score,
    },
  } as Part;
}

function imagePart(data: ChatImageData): Part {
  return { type: 'data-image', id: data.id, data } as Part;
}

/** A stored message's pictures as `data-image` parts. */
function imageParts(m: ConversationHistoryMessage): Part[] {
  const images = Object.entries(m.images ?? {}).map(([id, ref]) =>
    imagePart({
      ...ref,
      id,
      kind: ref.kind === 'source' ? 'source' : 'library',
      width: ref.width ?? undefined,
      height: ref.height ?? undefined,
      credit: ref.attribution,
      visual: null,
    })
  );
  const visuals = Object.entries(m.visuals ?? {}).map(([id, visual]) =>
    imagePart({
      id,
      kind: 'visual',
      url: 'src' in visual ? visual.src : '',
      caption: visual.caption,
      visual,
    })
  );
  return [...images, ...visuals];
}

export function historyToUIMessages(
  messages: ConversationHistoryMessage[]
): UIMessage[] {
  return messages.map(m => {
    const parts: UIMessage['parts'] = [
      // The AI SDK's text part shape requires a `state` of "done" for
      // already-completed text - without it, the renderer treats the
      // part as still-streaming.
      { type: 'text', text: m.content, state: 'done' } as Part,
      ...m.sources_used.map(citationPart),
      ...imageParts(m),
    ];
    return { id: m.id, role: m.role, parts } as UIMessage;
  });
}

export interface ExtractedMessage {
  text: string;
  sources: ChatSourceCitation[];
  /** `img://<id>` → a library image or a figure from the student's source. */
  images: Record<string, ResolvedLibraryImage>;
  /** `img://cv-<id>` → a concept's visual. */
  visuals: Record<string, Visual>;
}

function citationFrom(part: Part): ChatSourceCitation {
  const data = (
    part as { data: ChatSourceCitation & { index?: number | null } }
  ).data;
  return {
    // A streamed part's `index` is its marker (backend `sse`).
    marker: data.marker ?? data.index ?? null,
    source_id: data.source_id,
    source_name: data.source_name,
    chunk_id: data.chunk_id,
    text_preview: data.text_preview,
    page: data.page,
    relevance_score: data.relevance_score,
    source_type: data.source_type,
    url: data.url,
    file_key: data.file_key,
  };
}

function addImage(out: ExtractedMessage, part: Part): void {
  const data = (part as { data?: ChatImageData }).data;
  if (!data?.id) return;
  if (data.kind === 'visual') {
    if (data.visual) out.visuals[data.id] = data.visual;
  } else if (data.url) {
    out.images[data.id] = data;
  }
}

/** The rendered text, citations and pictures of a message - the same shape
 *  whether it arrived by stream or by history hydration. */
export function extractMessage(message: UIMessage): ExtractedMessage {
  const out: ExtractedMessage = {
    text: '',
    sources: [],
    images: {},
    visuals: {},
  };
  for (const part of message.parts ?? []) {
    if (part.type === 'text') {
      out.text += (part as { text: string }).text ?? '';
    } else if (part.type === 'data-citation') {
      out.sources.push(citationFrom(part));
    } else if (part.type === 'data-image') {
      addImage(out, part);
    }
  }
  // Legacy fallback for any code path still using `content`.
  const legacy = (message as unknown as { content?: string }).content;
  if (!out.text && legacy) out.text = legacy;
  return out;
}
